import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { Area, PurchaseLineResponse, ResourceLocation } from '@/generated/data.schemas';
import { useGetTranslations } from '@/generated/translations';
import { Image } from '../Image';
import { Link } from '../Link';
import { Price } from '../Price';
import { TextBadge } from '../TextBadge';
import styles from './BasketLine.module.scss';

export interface QuickBasketLineProps {
	line: PurchaseLineResponse;
	isLinkDisabled?: boolean;
	variant?: 'default' | 'applicationLine' | 'additional';
	label?: string;
}

export const QuickBasketLine: React.FunctionComponent<QuickBasketLineProps> = ({ line, variant, label, isLinkDisabled }) => {
	const { productMasterId, productVariantId, productNumber, image, name, url, quantity, price } = line;

	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);

	const productUrl = useMemo(
		() => (productVariantId ? `${url}?variant=${productVariantId}` : url),
		[productVariantId, url],
	);

	const route: ResourceLocation = useMemo(() => {
		return {
			area: Area.Product,
			productMasterId: productMasterId,
			externalRoute: productUrl || '/',
		};
	}, [productMasterId, productUrl]);

	return (
		<div className={styles.lineWrapper}>
			<div
				className={classNames(styles.wrapper, styles.hasNoRemove, styles.isSmall, {
					[styles.applicationLine]: variant === 'applicationLine',
						[styles.hasLabel]: !!label,
					})}
			>
				<div className={classNames(styles.productField, styles.quickBasketProductField)}>
						{label && (
							<div className={styles.labelOverlay}>
								<TextBadge text={label} />
							</div>
						)}
						<div className={styles.imageWrapper}>
							<Image
							className={styles.image}
							size180={image.size180}
							size320={image.size320}
							size480={image.size480}
							size720={image.size720}
							size960={image.size960}
							size1280={image.size1280}
							size1920={image.size1920}
							// Has to match --thumb-size
							responsiveSizesSettings={'56px'}
							altText={image.altText || name}
						/>
					</div>
					<div className={styles.infoWrapper}>
						<Link className={styles.link} route={!isLinkDisabled && productUrl ? route : undefined}>
							<p className={classNames(styles.name, 'u-ellipsis')}>{name}</p>
							<p className={styles.itemNo}>
								{translations?.data.shared.itemNo} {productNumber}
							</p>
							<p className={styles.quantityText}>
								{translations?.data.basket.quantityTitle}: {quantity}
							</p>
						</Link>
					</div>
				</div>
				<div className={classNames(styles.field, styles.padding, styles.end, styles.price, styles.itemPrice)}>
					<div className={styles.priceInfo}>
						<Price price={price} size={'sm'} />
					</div>
				</div>
			</div>
		</div>
	);
};
